import { useEffect } from "react";
import { X, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { User } from "@supabase/supabase-js";
import quentariLogo from "@/assets/quentari-logo.svg";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (id: string) => void;
  user: User | null;
  onAuthClick: () => void;
  onDashboardClick: () => void;
}

const navItems = [
  { label: "Prozess", id: "process", description: "Der Entscheidungsweg" },
  { label: "Leistungen", id: "features", description: "Was Quentari ermöglicht" },
  { label: "Ergebnisse", id: "outcomes", description: "Was sich verändert" },
  { label: "FAQ", id: "faq", description: "Häufige Fragen" },
];

export const MobileMenu = ({ 
  isOpen, 
  onClose, 
  onNavigate, 
  user, 
  onAuthClick, 
  onDashboardClick 
}: MobileMenuProps) => {
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleNavClick = (id: string) => {
    onNavigate(id);
    onClose();
  };

  const handleAuthClick = () => {
    onAuthClick();
    onClose();
  };

  const handleDashboardClick = () => {
    onDashboardClick();
    onClose();
  };

  return (
    <>
      {/* Overlay with blur */}
      <div
        className={cn(
          "fixed inset-0 bg-foreground/10 backdrop-blur-md z-40 transition-all duration-400",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      {/* Slide-in Menu - Premium Design */}
      <div
        className={cn(
          "fixed top-0 right-0 h-full w-[320px] max-w-[90vw] bg-background/95 backdrop-blur-xl border-l border-border/50 z-50 transform transition-all duration-400 ease-out",
          isOpen ? "translate-x-0 shadow-2xl shadow-foreground/10" : "translate-x-full"
        )}
      >
        {/* Decorative gradient */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 100% 0%, hsl(var(--primary) / 0.03) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 0% 100%, hsl(var(--primary) / 0.02) 0%, transparent 40%)
            `,
          }}
        />

        <div className="flex flex-col h-full relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-border/30">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-6 w-auto"
            />
            <button
              onClick={onClose}
              className="p-2.5 rounded-xl bg-muted/30 hover:bg-muted/50 transition-all duration-300 active:scale-95 group"
              aria-label="Menü schließen"
            >
              <X className="w-4 h-4 text-foreground/70 group-hover:text-foreground transition-colors" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 py-6 px-4 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground/60 uppercase tracking-widest px-3 mb-4">
              Navigation
            </p>
            <ul className="space-y-1">
              {navItems.map((item, index) => (
                <li 
                  key={item.id}
                  className={cn(
                    "transition-all duration-300",
                    isOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                  )}
                  style={{ transitionDelay: isOpen ? `${(index + 1) * 50}ms` : '0ms' }}
                >
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className="w-full flex items-center justify-between py-4 px-4 rounded-xl hover:bg-muted/30 active:bg-muted/50 transition-all duration-200 group"
                  >
                    <div className="text-left">
                      <span className="block text-base font-medium text-foreground group-hover:text-primary transition-colors">
                        {item.label}
                      </span>
                      <span className="block text-xs text-muted-foreground/70 mt-0.5">
                        {item.description}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* CTA Buttons */}
          <div 
            className={cn(
              "p-5 border-t border-border/30 space-y-3 bg-muted/20 transition-all duration-300",
              isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: isOpen ? '250ms' : '0ms' }}
          >
            {user ? (
              <Button
                className="w-full h-12 font-semibold group"
                onClick={handleDashboardClick}
              >
                <span>Zum Dashboard</span>
                <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="w-full h-11 font-medium border-border/50 hover:bg-muted/30"
                  onClick={handleAuthClick}
                >
                  Anmelden
                </Button>
                <Button
                  className="w-full h-12 font-semibold group relative overflow-hidden"
                  onClick={() => window.location.href = '/auth'}
                >
                  {/* Shimmer effect */}
                  <span 
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent pointer-events-none"
                    aria-hidden="true"
                  />
                  <span className="relative z-10">Erste Entscheidung strukturieren</span>
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 relative z-10" />
                </Button>
              </>
            )}
            
            {/* Trust note */}
            <p className="text-center text-[10px] text-muted-foreground/50 pt-2">
              Keine Kreditkarte erforderlich
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
